/* Card rows for wide tables below 720 px (QA-030, APP-067 §7). The table that opts in gets
   `.cards`; each row becomes a card, each value sits under its column's name, and the row's action
   goes to the bottom at full width. Tokens only; every target at least 44 px. */

.cards,
.cards > tbody,
.cards > tbody > tr,
.cards > tbody > tr > td,
.cards > tbody > tr > th {
  display: block;
  width: 100%;
  min-width: 0;
}

/* Wide tables set a min-width for table mode; cards fit the card instead. */
.cards {
  min-width: 0 !important;
  table-layout: auto;
}

/* The header row stays for screen readers (each value is tied to it), out of sight. */
.head {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.cards > tbody > tr {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--surface-card, var(--white));
}

.cards > tbody > tr:last-child {
  margin-bottom: 0;
}

.cards > tbody > tr[hidden] {
  display: none;
}

.cards > tbody > tr > td,
.cards > tbody > tr > th {
  padding: 0 !important;
  border: 0 !important;
  background: none !important;
  text-align: left !important;
  white-space: normal !important;
}

/* A labelled value: the column's name above it. A value is as wide as its words, so a status pill
   hugs its text as the boards draw chips; long words still wrap at the card's edge. */
.cards > tbody > tr > [data-card='value'] {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  justify-items: start;
  gap: 2px;
}

/* A field in a value (an inline edit) keeps the card's full width. */
.cards > tbody > tr > [data-card='value'] > :has(input, select, textarea) {
  justify-self: stretch;
}

.cards > tbody > tr > [data-card='value']::before {
  /* Shown, not read again: the cell is already tied to its header (the plain form first, for
     a browser without alternative text for generated content). */
  content: attr(data-label);
  content: attr(data-label) / '';
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: var(--fw-medium);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/* An empty-looking cell (a hidden selection box, nothing to show) takes no room. */
.cards > tbody > tr > [data-card='value']:empty {
  display: none;
}

.cards > tbody > tr > [data-card='title'] {
  order: -1;
  font-size: 16px;
}

/* The row's action: last, at full width, easy to reach. */
.cards > tbody > tr > [data-card='action'] {
  order: 99;
  padding-top: 4px !important;
}

.cards > tbody > tr > [data-card='action'] :is(a, button) {
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  justify-content: center;
}

.cards > tbody > tr > [data-card='wide'] {
  display: block;
}

/* The toggle that opens a row's details (APP-109): just above the action, so the details open
   right under it. A row without details has an empty toggle cell, which takes no room. */
.cards > tbody > tr > [data-card='toggle'] {
  order: 98;
}

.cards > tbody > tr > [data-card='toggle']:empty {
  display: none;
}

/* A row's open details: a card of their own, joined to the row's card above, so the two read as
   one. The row's card gives up its bottom corners and gap; the details keep the row's rule. */
.cards > tbody > tr:has(+ tr:not([hidden]) > [data-card='details']) {
  margin-bottom: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.cards > tbody > tr:has(> [data-card='details']) {
  border-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.cards > tbody > tr > [data-card='details'] {
  display: block;
}

/* The last card before a closed details row is the last one shown. */
.cards > tbody > tr:has(+ tr[hidden]:last-child) {
  margin-bottom: 0;
}

/* A group heading ("Ready to pay · 3") stands above its cards, not inside one. */
.cards > tbody > tr:has(> .group) {
  padding: 8px 0 0;
  border: 0;
  background: none;
}

@media (prefers-reduced-motion: no-preference) {
  .cards > tbody > tr {
    transition: border-color 120ms ease;
  }
}
